import { fileUploadHandler } from '../hooks';
import {
  BoldExtension,
  ItalicExtension,
  OrderedListExtension,
  TaskListExtension,
  BulletListExtension,
  MarkdownExtension,
  BlockquoteExtension,
  ImageExtension,
  HardBreakExtension,
  NodeFormattingExtension,
  PlaceholderExtension,
  StrikeExtension,
  SubExtension,
  SupExtension,
  UnderlineExtension,
  CodeExtension,
  HistoryExtension,
  HorizontalRuleExtension,
  IframeExtension,
  TextHighlightExtension,
} from 'remirror/extensions';
import css from 'refractor/lang/css.js';
import javascript from 'refractor/lang/javascript.js';
import json from 'refractor/lang/json.js';
import python from 'refractor/lang/python';
import java from 'refractor/lang/java';
import markdown from 'refractor/lang/markdown.js';
import typescript from 'refractor/lang/typescript.js';
import VideoExtension from './video-extension';
import markdownToHtml from './markdown-to-html';
import htmlToMarkdown from './html-to-markdown';
import { TableExtension } from '@remirror/extension-react-tables';
import MarkdownPasteExtension from './markdown-paste-extension';
import { FindExtension } from '@remirror/extension-find';
import MentionExtension from './mention-extension';
import { message } from 'antd';
import TableNavigationExtension from './TableNavigationExtension';
import CodeblockExtension from './code-block-extension';
import { FontSizeExtension } from '@remirror/extension-font-size';
import { TextColorExtension } from '@remirror/extension-text-color';
import { editorConfig } from '../editorConfig';
import { ActiveNodeExtension, HeadingExtension } from './../extensions';
import { CustomLinkExtension } from './custom-link-extension';
import TextStyleExtension from './text-style-extension';
import { DragNDropExtension } from '../components/Editor/DragAndDropExtention';
import { InlineCommentExtension } from './../extensions/InlineCommentExtension';
import { CalloutExtension } from './../extensions/CalloutExtension';
import { ReactComponentExtension } from '@remirror/extension-react-component';
import {
  AccordionContentComponent,
  AccordionTitleComponent,
  AccordionExtension,
  AccordionContentExtension,
  AccordionTitleExtension,
} from './../extensions/AccordionExtension';

const uploadHandler =
  (file, { file_id, workspace_id, organization_id }) =>
  () =>
    new Promise((resolve, reject) => {
      const upload = fileUploadHandler(file, {
        body: { fileId: file_id, workspaceId: workspace_id, organizationId: organization_id, suppressError: true },
        attrs: { width: '600px', align: 'center' },
        mock: false,
      });

      upload()
        .then((result) => {
          resolve({ src: result.src, fileName: file?.originFileObj?.name });
          message.success('File uploaded successfully');
        })
        .catch((err) => {
          message.error('Unable to upload media due to server error.');
          reject(err);
        });
    });

export const extensionsList = ({
  file_id,
  workspace_id,
  organization_id,
  user,
  isEditable,
  disableRichFeatures = false,
}) => {
  const extensions = [];

  if (editorConfig.features.markdown) {
    extensions.push(
      new MarkdownExtension({
        htmlToMarkdown: (html) => htmlToMarkdown(html),
        markdownToHtml: (markdown) => markdownToHtml(markdown),
        copyAsMarkdown: true,
      }),
      new MarkdownPasteExtension()
    );
  }

  if (editorConfig.features.headings) extensions.push(new HeadingExtension());
  if (editorConfig.features.bold) extensions.push(new BoldExtension());
  if (editorConfig.features.italic) extensions.push(new ItalicExtension());
  if (editorConfig.features.bulletList) extensions.push(new BulletListExtension());
  if (editorConfig.features.orderedList) extensions.push(new OrderedListExtension());
  if (editorConfig.features.taskList) extensions.push(new TaskListExtension());
  if (editorConfig.features.blockquote) extensions.push(new BlockquoteExtension());
  if (editorConfig.features.code) extensions.push(new CodeExtension());
  // if (editorConfig.features.dropCursor) extensions.push(new DropCursorExtension());
  if (editorConfig.features.hardBreak) extensions.push(new HardBreakExtension());
  if (editorConfig.features.nodeFormatting) extensions.push(new NodeFormattingExtension());
  if (editorConfig.features.strike) extensions.push(new StrikeExtension());
  if (editorConfig.features.subscript) extensions.push(new SubExtension());
  if (editorConfig.features.superscript) extensions.push(new SupExtension());
  if (editorConfig.features.fontSize) extensions.push(new FontSizeExtension({ defaultSize: '16', unit: 'px' }));
  if (editorConfig.features.textColor)
    extensions.push(new TextColorExtension({ defaultColor: 'var(--editor-heading-font-color)' }));
  if (editorConfig.features.underline) extensions.push(new UnderlineExtension());
  if (editorConfig.features.history) extensions.push(new HistoryExtension());
  if (editorConfig.features.horizontalRule) extensions.push(new HorizontalRuleExtension());
  if (editorConfig.features.mention) extensions.push(new MentionExtension({ activeUser: user }));

  if (editorConfig.features.table) {
    extensions.push(
      new TableExtension({
        resizable: true,
        resizeableOptions: { cellMinWidth: 30 },
      })
    );
  }

  if (editorConfig.features.link) {
    extensions.push(
      new CustomLinkExtension({
        autoLink: true,
        selectTextOnClick: false,
        openLinkOnClick: true,
        defaultTarget: '_blank',
      })
      // new LinkExtension()
    );
  }

  if (editorConfig.features.placeholder) {
    extensions.push(
      new PlaceholderExtension({ placeholder: "Write, Press '/' for commands...", emptyNodeClass: 'placeholder-node' })
    );
  }

  if (editorConfig.features.codeblock) {
    extensions.push(
      new CodeblockExtension({
        defaultLanguage: 'json',
        supportedLanguages: [css, javascript, json, markdown, typescript, python, java],
      })
    );
  }

  if (editorConfig.features.find) extensions.push(new FindExtension({}));
  if (editorConfig.features.tableNavigation) extensions.push(new TableNavigationExtension({ isEditable }));

  if (editorConfig.features.image) {
    extensions.push(
      new ImageExtension({
        enableResizing: true,
        uploadHandler: (files) => {
          const promises = [];
          for (const { file } of files) {
            promises.push(uploadHandler({ originFileObj: file }, { file_id, workspace_id, organization_id }));
          }
          return promises;
        },
      })
    );
  }

  if (editorConfig.features.iframe) extensions.push(new IframeExtension({ enableResizing: true }));
  if (editorConfig.features.video) extensions.push(new VideoExtension({}));
  if (editorConfig.features.findExtension) extensions.push(new FindExtension());
  if (editorConfig.features.tableNavigationExtension) extensions.push(new TableNavigationExtension({ isEditable }));
  if (editorConfig.features.dateNodeExtension) extensions.push(new TextStyleExtension());

  if (!disableRichFeatures) {
    extensions.push(
      new DragNDropExtension({
        isDraggableBlock: (node) => {
          return node.tagName === 'P' || node.tagName === 'BLOCKQUOTE';
        },
      })
    );
  }
  if (editorConfig.features.calloutPanel) {
    extensions.push(new CalloutExtension({ validTypes: ['info', 'danger', 'warning', 'success'] }));
  }
  if (editorConfig.features.inlineComment) {
    extensions.push(new InlineCommentExtension());
  }
  if (editorConfig.features.accordion) {
    extensions.push(
      new ReactComponentExtension({
        nodeViewComponents: {
          accordionTitle: AccordionTitleComponent,
          accordionContent: AccordionContentComponent,
        },
      })
    );
    extensions.push(new AccordionTitleExtension());
    extensions.push(new AccordionContentExtension());
    extensions.push(new AccordionExtension());
  }

  if (editorConfig.features.textHighlight) {
    extensions.push(new TextHighlightExtension());
  }
  extensions.push(new ActiveNodeExtension());
  return extensions;
};
